import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { mediaService } from '@/services/media/mediaService';

interface PersistImageTransformParams {
  assetId: string;
  publicUrl: string;
  queryClient: QueryClient;
  onPersisted?: () => void;
}

const invalidateImageLibraryQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: ['library'] });
  queryClient.invalidateQueries({ queryKey: ['media-library'] });
  queryClient.invalidateQueries({ queryKey: ['media'] });
};

interface PersistImageTransformBlobParams {
  assetId: string;
  blob: Blob;
  filename: string;
  previousUrl: string;
  queryClient: QueryClient;
  setImageUrl: (
    url: string,
    options?: {
      historyUrl?: string;
      skipHistory?: boolean;
    },
  ) => void;
  onPersisted?: () => void;
  revokeAfterMs?: number;
}

export const persistImageTransform = async ({
  assetId,
  publicUrl,
  queryClient,
  onPersisted,
}: PersistImageTransformParams): Promise<void> => {
  try {
    const updated = await mediaService.updateMediaUrl(assetId, publicUrl);
    if (updated) {
      onPersisted?.();
      invalidateImageLibraryQueries(queryClient);
    }
    // No row matched → session-only asset (e.g. chat artifact); the edit
    // still shows in the dialog and exports fine — nothing to sync.
  } catch (err) {
    // The transform itself succeeded; only the library sync failed.
    console.error('[ImageBody] failed to persist edit to media row', err);
    toast.error('Edited image may not show in your Library — check your connection.');
  }
};

export const persistImageTransformBlob = async ({
  assetId,
  blob,
  filename,
  previousUrl,
  queryClient,
  setImageUrl,
  onPersisted,
  revokeAfterMs = 5000,
}: PersistImageTransformBlobParams): Promise<void> => {
  const localUrl = URL.createObjectURL(blob);
  setImageUrl(localUrl, { skipHistory: true });
  try {
    const publicUrl = await mediaService.uploadGeneratedContentBlob(blob, filename);
    setImageUrl(publicUrl, { historyUrl: previousUrl });
    await persistImageTransform({ assetId, publicUrl, queryClient, onPersisted });
  } finally {
    setTimeout(() => URL.revokeObjectURL(localUrl), revokeAfterMs);
  }
};
